// Shared schema.org helpers for the structured data every page emits.

export interface Crumb {
  name: string;
  url: string;
}

function humanize(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Home > Section > … > current page. The current page takes the page's own
 * title; ancestors use their path segment humanized. Returns [] for the home
 * page, since a one-item trail adds nothing. */
export function breadcrumbsFor(origin: string, path: string, currentTitle: string): Crumb[] {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return [];
  const crumbs: Crumb[] = [{ name: 'Home', url: `${origin}/` }];
  let acc = '';
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    const isLast = i === segments.length - 1;
    crumbs.push({ name: isLast ? currentTitle : humanize(seg), url: `${origin}${acc}` });
  });
  return crumbs;
}

export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
  };
}

export function webPageSchema(opts: { origin: string; url: string; name: string; description: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${opts.url}#webpage`,
    url: opts.url,
    name: opts.name,
    description: opts.description,
    inLanguage: 'en-IN',
    isPartOf: { '@id': `${opts.origin}/#website` },
  };
}

export function websiteSchema(origin: string, name: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    url: origin,
    name,
    inLanguage: 'en-IN',
  };
}
